import React, {useState, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Animated,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {CommonActions} from '@react-navigation/native';
import {Colors} from '../theme/colors';
import {useAuth} from '../hooks/useAuth';
import {Icon} from '../components/Icon';

type RootStackParamList = {
  Home: undefined;
  Login: undefined;
  Register: undefined;
  ForgotPassword: undefined;
  Article: any;
};

type LoginScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Login'>;
};

export const LoginScreen: React.FC<LoginScreenProps> = ({navigation}) => {
  const insets = useSafeAreaInsets();
  const {login} = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Floating label animations
  const emailLabelAnim = useRef(new Animated.Value(email ? 1 : 0)).current;
  const passwordLabelAnim = useRef(new Animated.Value(password ? 1 : 0)).current;

  const animateLabel = (anim: Animated.Value, toValue: number) => {
    Animated.timing(anim, {
      toValue,
      duration: 150,
      useNativeDriver: false,
    }).start();
  };

  const handleEmailFocus = () => {
    setEmailFocused(true);
    animateLabel(emailLabelAnim, 1);
  };
  const handleEmailBlur = () => {
    setEmailFocused(false);
    if (!email) animateLabel(emailLabelAnim, 0);
  };
  const handlePasswordFocus = () => {
    setPasswordFocused(true);
    animateLabel(passwordLabelAnim, 1);
  };
  const handlePasswordBlur = () => {
    setPasswordFocused(false);
    if (!password) animateLabel(passwordLabelAnim, 0);
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setError('Inserisci email e password');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await login(email.trim(), password);
    
    setIsLoading(false);

    if (result.success) {
      navigation.goBack();
    } else {
      setError(result.message || 'Credenziali non valide');
    }
  };

  const handleForgotPassword = () => {
    navigation.dispatch(
      CommonActions.navigate({name: 'ForgotPassword'})
    );
  };

  const handleRegister = () => {
    navigation.dispatch(
      CommonActions.navigate({name: 'Register'})
    );
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          {/* Purple Header with Logo */}
          <View style={[styles.purpleHeader, {paddingTop: insets.top + 16}]}>
            <TouchableOpacity 
              style={[styles.closeButton, {top: insets.top + 8}]}
              onPress={() => navigation.goBack()}
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}
            >
              <Icon name="xmark" size={20} color="#FFFFFF" />
            </TouchableOpacity>
            <Image
              source={{uri: 'https://cdn.laviola.it/laviola/logo-payoff.png'}}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>

          {/* White Form Area */}
          <View style={styles.formContainer}>
            <View style={styles.form}>
              {/* Title with lock icon */}
              <View style={styles.titleRow}>
                <Icon name="lock" size={22} color={Colors.primary} />
                <Text style={styles.title}>Accedi al tuo account</Text>
              </View>
              
              <Text style={styles.subtitle}>
                I tuoi dati personali verranno utilizzati per supportare la tua esperienza su questo sito web.
              </Text>

              {error ? (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : null}

              {/* Email field */}
              <View style={[
                styles.inputContainer,
                emailFocused && styles.inputContainerFocused,
              ]}>
                <Icon 
                  name="envelope" 
                  size={16} 
                  color={emailFocused ? Colors.primary : Colors.textSecondary} 
                />
                <View style={styles.floatingWrapper}>
                  <Animated.Text style={[
                    styles.floatingLabel,
                    {
                      top: emailLabelAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [14, 2],
                      }),
                      fontSize: emailLabelAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [16, 11],
                      }),
                      color: emailFocused ? Colors.primary : Colors.textSecondary,
                    },
                  ]}>Email</Animated.Text>
                  <TextInput
                    style={[styles.input, (emailFocused || email) && styles.inputWithLabel]}
                    value={email}
                    onChangeText={setEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    onFocus={handleEmailFocus}
                    onBlur={handleEmailBlur}
                  />
                </View>
              </View>

              {/* Password field */}
              <View style={[
                styles.inputContainer,
                passwordFocused && styles.inputContainerFocused,
              ]}>
                <Icon 
                  name="lock" 
                  size={16} 
                  color={passwordFocused ? Colors.primary : Colors.textSecondary} 
                />
                <View style={styles.floatingWrapper}>
                  <Animated.Text style={[
                    styles.floatingLabel,
                    {
                      top: passwordLabelAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [14, 2],
                      }),
                      fontSize: passwordLabelAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [16, 11],
                      }),
                      color: passwordFocused ? Colors.primary : Colors.textSecondary,
                    },
                  ]}>Password</Animated.Text>
                  <TextInput
                    style={[styles.input, (passwordFocused || password) && styles.inputWithLabel]}
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    onFocus={handlePasswordFocus}
                    onBlur={handlePasswordBlur}
                  />
                </View>
                <TouchableOpacity 
                  onPress={() => setShowPassword(!showPassword)}
                  style={styles.eyeButton}
                >
                  <Icon 
                    name={showPassword ? 'eye' : 'eye-slash'} 
                    size={14} 
                    color={Colors.textSecondary} 
                  />
                </TouchableOpacity>
              </View>

              {/* Remember me toggle */}
              <View style={styles.optionsRow}>
                <TouchableOpacity 
                  style={styles.rememberMeContainer}
                  onPress={() => setRememberMe(!rememberMe)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.toggle, rememberMe && styles.toggleActive]}>
                    <View style={[styles.toggleThumb, rememberMe && styles.toggleThumbActive]} />
                  </View>
                  <Text style={styles.rememberMeText}>Ricordami</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.loginButton, isLoading && styles.loginButtonDisabled]}
                onPress={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <View style={styles.loginButtonContent}>
                    <Text style={styles.loginButtonText}>Accedi</Text>
                    <Icon name="chevron-right" size={14} color="#FFFFFF" />
                  </View>
                )}
              </TouchableOpacity>

              {/* Forgot password link */}
              <TouchableOpacity onPress={handleForgotPassword} style={styles.forgotPasswordContainer}>
                <Text style={styles.linkText}>Password dimenticata?</Text>
              </TouchableOpacity>
            </View>

            {/* Register link - outside form */}
            <View style={styles.registerContainer}>
              <Text style={styles.registerText}>Non hai un account? </Text>
              <TouchableOpacity onPress={handleRegister}>
                <Text style={styles.registerLink}>Registrati ora</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  purpleHeader: {
    backgroundColor: Colors.primary,
    alignItems: 'center',
    paddingBottom: 40,
    paddingHorizontal: 20,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderRadius: 22,
    zIndex: 10,
  },
  logo: {
    width: 200,
    height: 70,
    marginTop: 0,
  },
  formContainer: {
    flex: 1,
    backgroundColor: Colors.background,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    marginTop: -20,
    paddingTop: 28,
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
  form: {
    backgroundColor: Colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.textPrimary,
  },
  subtitle: {
    fontSize: 13,
    color: Colors.textSecondary,
    lineHeight: 18,
    marginBottom: 20,
  },
  errorContainer: {
    backgroundColor: '#FFEBEE',
    padding: 12,
    borderRadius: 8,
    marginBottom: 14,
  },
  errorText: {
    color: Colors.error,
    fontSize: 13,
    textAlign: 'center',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.surfaceVariant,
    borderRadius: 10,
    paddingHorizontal: 14,
    marginBottom: 16,
    gap: 10,
    borderWidth: 2,
    borderColor: 'transparent',
    minHeight: 56,
  },
  inputContainerFocused: {
    borderColor: Colors.primary,
    backgroundColor: Colors.surface,
  },
  floatingWrapper: {
    flex: 1,
    justifyContent: 'center',
    position: 'relative',
    height: 52,
  },
  floatingLabel: {
    position: 'absolute',
    left: 0,
    fontWeight: '500',
  },
  input: {
    flex: 1,
    paddingTop: 14,
    paddingBottom: 6,
    fontSize: 16,
    color: Colors.textPrimary,
  },
  inputWithLabel: {
    paddingTop: 20,
    paddingBottom: 4,
  },
  optionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: Colors.primary,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: Colors.primary,
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#E0E0E0',
    padding: 2,
    marginRight: 10,
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: Colors.primary,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  rememberMeText: {
    fontSize: 14,
    color: Colors.textSecondary,
  },
  eyeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: '500',
    textDecorationLine: 'underline',
  },
  forgotPasswordContainer: {
    alignItems: 'center',
    marginTop: 16,
  },
  loginButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 16,
    borderRadius: 28,
    alignItems: 'center',
  },
  loginButtonDisabled: {
    opacity: 0.7,
  },
  loginButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  loginButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  registerContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 20,
  },
  registerText: {
    fontSize: 14,
    color: Colors.textSecondary,
  },
  registerLink: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
